import React, { useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../../../contexts/AppContext';
import { Image } from 'antd';
import './ProductDetail.css';

const ProductDetail: React.FC = () => {
    const { productId } = useParams<{ productId: string }>();
    const { products } = useApp();
    const navigate = useNavigate();
    const [previewVisible, setPreviewVisible] = useState(false);
    const [isIconHovered, setIsIconHovered] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);
    const product = products.find(p => String(p.id) === productId);

    if (!product) {
        return (
            <div className="product-not-found">
                <h2>Product not found</h2>
                <button onClick={() => navigate('/shop')}>Back to Shop</button>
            </div>
        );
    }

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (isIconHovered) return;
        const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - left) / width) * 100;
        const y = ((e.clientY - top) / height) * 100;

        if (imgRef.current) {
            imgRef.current.style.transformOrigin = `${x}% ${y}%`;
        }
    };

    // Get related products (same category, excluding current, max 3)
    const relatedProducts = products
        .filter(p => p.category === product.category && p.id !== product.id)
        .slice(0, 3);

    return (
        <div className="product-detail-container">
            <div className="hidden-preview-trigger" style={{ display: 'none' }}>
                <Image
                    preview={{
                        visible: previewVisible,
                        src: product.image,
                        onVisibleChange: (value) => setPreviewVisible(value),
                    }}
                />
            </div>

            <div className="product-detail-wrapper">
                {/* Left Side: Product Image with Custom Zoom */}
                <div className="product-detail-image-section">
                    <div
                        className={`product-image-container ${isIconHovered ? 'zoom-disabled' : ''}`}
                        onMouseMove={handleMouseMove}
                    >
                        <img
                            ref={imgRef}
                            src={product.image}
                            alt={product.name}
                            className="detail-main-image"
                        />
                        <div
                            className="zoom-icon-trigger"
                            onMouseEnter={() => setIsIconHovered(true)}
                            onMouseLeave={() => setIsIconHovered(false)}
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsIconHovered(true);
                                setPreviewVisible(true);
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                <line x1="11" y1="8" x2="11" y2="14"></line>
                                <line x1="8" y1="11" x2="14" y2="11"></line>
                            </svg>
                        </div>
                    </div>
                </div>

                {/* Right Side: Product Info */}
                <div className="product-detail-info-section">
                    <h1 className="detail-title">{product.name}</h1>
                    <div className="detail-price">{product.price}</div>
                    <div className="detail-description">
                        <p>{product.description}</p>
                    </div>
                    {/* <div className="detail-actions">
                        <div className="quantity-selector">
                            <span className="qty-btn minus" onClick={() => setQuantity(Math.max(1, quantity - 1))}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <path d="M19 12H5" />
                                </svg>
                            </span>
                            <span className="qty-value">{quantity}</span>
                            <span className="qty-btn plus" onClick={() => setQuantity(quantity + 1)}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <path d="M12 5v14M5 12h14" />
                                </svg>
                            </span>
                        </div>
                        <button className="add-to-cart-btn" onClick={() => showMessage(`${product.name} added to cart!`)}>ADD TO CART</button>
                        <button
                            className={`detail-wishlist-btn ${isInWishlist ? 'active' : ''}`}
                            onClick={() => {
                                setIsInWishlist(!isInWishlist);
                                showMessage(isInWishlist ? `${product.name} removed from wishlist!` : `${product.name} added to wishlist!`);
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill={isInWishlist ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l8.78-8.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                            </svg>
                        </button>
                    </div> */}

                    <div className="detail-meta">
                        <div className="meta-item">
                            <span className="meta-label">Category:</span>
                            <span className="meta-value">{product.category}</span>
                        </div>
                        <div className="meta-item">
                            <span className="meta-label">Tags:</span>
                            <span className="meta-value">{product.tags.join(', ')}</span>
                        </div>
                        <div className="meta-item">
                            <span className="meta-label">Product ID:</span>
                            <span className="meta-value">{product.id}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Related Products Section */}
            {relatedProducts.length > 0 && (
                <div className="related-products-section">
                    <h2 className="related-title">Related Products</h2>
                    <div className="product-grid-container" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '30px' }}>
                        {relatedProducts.map(item => (
                            <div key={item.id} className="product-card">
                                <div className="product-image-wrapper">
                                    <Link to={`/shop/${item.id}`} onClick={() => window.scrollTo(0, 0)}>
                                        <img src={item.image} alt={item.name} />
                                    </Link>
                                </div>
                                <div className="product-info">
                                    <h3 className="product-title">{item.name}</h3>
                                    <div className="product-price-row">
                                        <span className="current-price">{item.price}</span>
                                    </div>
                                    <div className="product-actions">
                                        <Link to={`/shop/${item.id}`} className="read-more-btn" onClick={() => window.scrollTo(0, 0)}>Read More</Link>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductDetail;
